import { useState, useEffect, useCallback } from 'react';
import {
  submitAirdropApplication,
  checkWalletApplication,
  getAllAirdropApplications,
  updateAirdropStatus,
  getActiveCampaign,
  createAirdropCampaign,
  getAllCampaigns,
  updateCampaign,
} from '../lib/airdropService';
import { useToast } from './use-toast';

export const useAirdrop = () => {
  const [applications, setApplications] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const { toast } = useToast();

  // Load all applications (admin)
  const loadApplications = useCallback(async () => {
    try {
      setLoadingApplications(true);
      const apps = await getAllAirdropApplications();
      setApplications(apps);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load airdrop applications',
        variant: 'destructive',
      });
    } finally {
      setLoadingApplications(false);
    }
  }, [toast]);

  // Load campaigns
  const loadCampaigns = useCallback(async () => {
    try {
      const [allCampaigns, active] = await Promise.all([
        getAllCampaigns(),
        getActiveCampaign(),
      ]);
      setCampaigns(allCampaigns);
      setActiveCampaign(active);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load campaigns',
        variant: 'destructive',
      });
    }
  }, [toast]);

  // Submit airdrop application (user)
  const applyForAirdrop = async (walletAddress, userEmail) => {
    try {
      setLoadingSubmission(true);
      const application = await submitAirdropApplication(walletAddress, userEmail);
      toast({
        title: 'Success',
        description: 'Airdrop application submitted successfully!',
      });
      return application;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to submit airdrop application';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw error;
    } finally {
      setLoadingSubmission(false);
    }
  };

  // Check if wallet has applied
  const checkWalletStatus = useCallback(async (walletAddress) => {
    try {
      return await checkWalletApplication(walletAddress);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to check wallet status',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  // Update application status (admin)
  const updateApplicationStatus = useCallback(async (applicationId, status, additionalData) => {
    try {
      await updateAirdropStatus(applicationId, status, additionalData);
      await loadApplications();
      toast({
        title: 'Success',
        description: `Application status updated to ${status}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update application status',
        variant: 'destructive',
      });
    }
  }, [loadApplications, toast]);

  // Create new campaign (admin)
  const createCampaign = async (campaignData) => {
    try {
      setLoadingCampaigns(true);
      const campaign = await createAirdropCampaign(campaignData);
      await loadCampaigns();
      toast({
        title: 'Success',
        description: 'Airdrop campaign created successfully!',
      });
      return campaign;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create campaign',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // Update campaign (admin)
  const updateCampaignData = async (campaignId, updates) => {
    try {
      await updateCampaign(campaignId, updates);
      await loadCampaigns();
      toast({
        title: 'Success',
        description: 'Campaign updated successfully!',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update campaign',
        variant: 'destructive',
      });
    }
  };

  // Distribute airdrop to multiple applications
  const distributeAirdrop = async (applicationIds, amount, txSignatures) => {
    try {
      setLoadingApplications(true);
      const updatePromises = applicationIds.map((id) =>
        updateAirdropStatus(id, 'distributed', {
          distributedAmount: amount,
          txSignature: txSignatures[id],
        })
      );

      await Promise.all(updatePromises);
      await loadApplications();

      toast({
        title: 'Success',
        description: `Airdrop distributed to ${applicationIds.length} recipients`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to distribute airdrop',
        variant: 'destructive',
      });
    } finally {
      setLoadingApplications(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  return {
    applications,
    campaigns,
    activeCampaign,
    loadingApplications,
    loadingCampaigns,
    loadingSubmission,
    loadApplications,
    loadCampaigns,
    applyForAirdrop,
    checkWalletStatus,
    updateApplicationStatus,
    createCampaign,
    updateCampaignData,
    distributeAirdrop,
  };
};
